// Транспорт и хуки раздела «Банк и бухгалтерия» — зеркало портала buh. ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/money.ts: там касса и ФОТ, видит одна учётка владельца. Здесь
// расчётный счёт компании и сальдо счетов бухучёта — это бухгалтерия и владелец, дверь
// другая (rights: раздел 'bank', группа 'бухгалтерия'). Сложить их в один файл значило бы
// однажды переиспользовать хук не там и открыть кассу бухгалтеру.
//
// Бухгалтер продолжает работать на портале buh: раздел в CRM — обзор и поиск. Ни одной
// записи здесь нет и появиться не должно: разнесение платежа меняет проводки в 1С.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/pbh'

/** Тот же разбор ответа, что в api/client.ts, api/p73.ts и api/p73x.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 — кривой фильтр, 403 c error='closed' — закрытая таблица (штрафы, сканы).
    // И то, и другое надо ОБЪЯСНИТЬ, а не показать пустую таблицу: пустая таблица здесь
    // читается как «таких операций по счёту не было», а это ложь про деньги.
    if (res.status === 400 || (res.status === 403 && rec.error === 'closed')) {
      throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. pbh_api.py) ───────────

/** Одна операция по расчётному счёту. */
export interface ОперацияБанка {
  src_id: number
  docnum: string | null
  op_date: string | null
  /** Дата как её напечатал банк. Нужна, когда op_date не разобралась. */
  op_date_raw: string | null
  /** 'in' — приход, 'out' — расход. */
  dir: string | null
  amount: number | null
  cp_name: string | null
  cp_inn: string | null
  purpose: string | null
  /** Номера счетов и сделок из назначения платежа, СВОБОДНЫМ ТЕКСТОМ: '3646,109300'. */
  refs: string | null
  /** Разобранный refs. Разбирает ядро — чтобы разбор был один на всю систему. */
  refs_list: string[]
  /** 'none' | 'hyp' | 'auto' | 'done' — состояние разбора в портале. */
  status: string | null
  cat: string | null
  why: string | null
  decision: string | null
  decided_by: string | null
  decided_at: string | null
  bank: string | null
  created_at: string | null
  /**
   * Номер сделки, который портал ВЫТАЩИЛ ИЗ ТЕКСТА назначения. Это гипотеза, а не связь.
   */
  deal_guess: number | null
  /**
   * Гипотеза совпала с суммой платежа: «…Сумма 109300-00» дало «сделку 109300».
   * Таких 77 из 1 580 (на 28.07.2026), и у 53 из них номер существует в Bitrix как
   * настоящая чужая сделка. Экран ОБЯЗАН показать этот флаг, иначе он соврёт ссылкой.
   */
  deal_guess_is_amount: boolean | null
  /** Название сделки, если такая в ядре есть. Наличие названия ничего не доказывает. */
  deal_guess_title: string | null
}

export interface ИтогиБанка {
  n: number | null
  in_n: number | null
  out_n: number | null
  in_rub: number | null
  out_rub: number | null
  unlinked_n: number | null
  unlinked_rub: number | null
  with_refs_n: number | null
  deal_guess_n: number | null
  guess_is_amount_n: number | null
  parties: number | null
  first_day: string | null
  last_day: string | null
}

export interface МесяцНеразнесённого {
  month: string
  n: number
  rub: number | null
  /** Сколько операций всего было в этом месяце — без него доля непонятна. */
  month_total: number
  /** Доля неразнесённого в месяце, %. Считает ядро: два экрана округлили бы по-разному. */
  share_pct: number | null
}

export interface КонтрагентНеразнесённого {
  cp_name: string
  cp_inn: string
  n: number
  rub: number | null
  first_day: string | null
  last_day: string | null
}

export interface ПричинаНеразнесённого {
  why: string
  n: number
  rub: number | null
}

export interface Неразнесённое {
  totals: {
    n: number | null
    in_n: number | null
    out_n: number | null
    in_rub: number | null
    out_rub: number | null
    with_refs_n: number | null
    with_refs_rub: number | null
    no_refs_n: number | null
    no_refs_rub: number | null
    parties: number | null
    first_day: string | null
    last_day: string | null
  }
  by_month: МесяцНеразнесённого[]
  top_parties: КонтрагентНеразнесённого[]
  by_reason: ПричинаНеразнесённого[]
  data_notes: Record<string, string>
  restricted?: string
  restricted_why?: string
}

/** Один счёт бухучёта в сальдовом аудите. */
export interface СчётСальдо {
  code: string
  name: string | null
  /** 'Active' | 'Passive' | 'ActivePassive' — от этого зависит, что считать ошибкой. */
  acct_type: string | null
  debit: number | null
  credit: number | null
  saldo: number | null
  /** 'ok' | 'warn' | 'err' — вердикт аудита портала. */
  status: string | null
  note: string | null
  monthly: unknown
  by_user: string | null
  updated_at: string | null
}

export interface ИтогиСальдо {
  n: number | null
  err_n: number | null
  warn_n: number | null
  ok_n: number | null
  /** Дата прогона аудита. Это снимок, а не «сальдо на сегодня». */
  snapshot_at: string | null
  authors: number | null
}

export interface ОтметкаАудита {
  src_id?: number
  fix_key?: string
  kind: string | null
  chk_key: string | null
  title: string | null
  val: number | null
  action?: string | null
  by_user: string | null
  at: string | null
}

export interface СнимокАудита {
  src_id: number
  kind: string | null
  ts: string | null
  data: Record<string, number>
  /** Разница с предыдущим снимком. null — предыдущего нет, сравнивать не с чем. */
  delta: Record<string, number> | null
}

export interface Аудит {
  log: ОтметкаАудита[]
  marks: ОтметкаАудита[]
  snapshots: СнимокАудита[]
  seen: { kind: string; n: number; last_at: string | null }[]
  data_notes: Record<string, string>
  restricted?: string
  restricted_why?: string
}

export interface Заливка {
  src_id: number
  fname: string | null
  bank: string | null
  period: string | null
  total_rows: number | null
  added: number | null
  dup: number | null
  by_user: string | null
  created_at: string | null
}

export interface МесяцПокрытия {
  month: string
  n: number
  first_day: string | null
  last_day: string | null
  in_rub: number | null
  out_rub: number | null
}

export interface Заливки {
  rows: Заливка[]
  coverage: МесяцПокрытия[]
  data_notes: Record<string, string>
  restricted?: string
  restricted_why?: string
}

export interface СвежестьPBH {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

/** Таблица зеркала, которую ядро не читает вовсе. */
export interface ЗакрытаяТаблица {
  entity: string
  table: string
  why: string
  where_the_door: string
  /** Сколько там строк — из sync_state, а не из самой таблицы. */
  rows: number | null
}

export interface ЧеловекPBH {
  src_id: number
  login: string | null
  full_name: string | null
  role: string | null
  is_active: boolean | null
  last_login: string | null
}

export interface СправочникиPBH {
  statuses: Record<string, number>
  dirs: Record<string, number>
  cats: Record<string, number>
  months: Record<string, number>
  balance_status: Record<string, number>
  counts: {
    bank_rows: number
    unlinked: number
    unlinked_rub: number | null
    unlinked_with_refs: number
    deal_guess: number
    guess_is_amount: number
    parties: number
    first_day: string | null
    last_day: string | null
    accounts: number
    accounts_err: number
    accounts_warn: number
    balance_at: string | null
    audit_log: number
    audit_marks: number
    audit_snaps: number
    uploads: number
    rules: number
    users: number
  }
  freshness: СвежестьPBH[]
  people: ЧеловекPBH[]
  closed: ЗакрытаяТаблица[]
  data_notes: Record<string, string>
  restricted?: string
  restricted_why?: string
}

/** Общая обёртка списка ядра + пометка «здесь деньги компании». */
export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  restricted?: string
  restricted_why?: string
}

export interface ФильтрыБанка {
  /** Индекс нужен, чтобы фильтры уходили в строкаЗапроса() как обычный набор пар. */
  [ключ: string]: unknown
  status?: string
  dir?: string
  cat?: string
  inn?: string
  month?: string
  from?: string
  to?: string
  min?: number | string
  max?: number | string
  ref?: string
  deal_bx?: number | string
  q?: string
  unlinked?: boolean
  start?: number
  limit?: number
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

// Выписка меняется медленно (полное перечитывание раз в 6 часов), поэтому минута кеша
// здесь не обман, а экономия: экран с четырьмя вкладками иначе бьёт в ядро на каждый клик.
const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function usePBHRefs() {
  return useQuery<СправочникиPBH>({
    queryKey: ['pbh', 'refs'],
    queryFn: () => запрос<СправочникиPBH>('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useВыписка(ф: ФильтрыБанка) {
  return useQuery<Список<ОперацияБанка> & { totals: ИтогиБанка }>({
    queryKey: ['pbh', 'bank', ф],
    queryFn: () => запрос('/bank' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useНеразнесённое(включено = true) {
  return useQuery<Неразнесённое>({
    queryKey: ['pbh', 'unlinked'],
    queryFn: () => запрос<Неразнесённое>('/unlinked'),
    enabled: включено,
    ...ОБЩЕЕ,
  })
}

export function useСальдо(ф: Record<string, unknown>) {
  return useQuery<Список<СчётСальдо> & { totals: ИтогиСальдо; data_notes: Record<string, string> }>({
    queryKey: ['pbh', 'balances', ф],
    queryFn: () => запрос('/balances' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useАудит(ф: Record<string, unknown> = {}) {
  return useQuery<Аудит>({
    queryKey: ['pbh', 'audit', ф],
    queryFn: () => запрос<Аудит>('/audit' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useЗаливки() {
  return useQuery<Заливки>({
    queryKey: ['pbh', 'uploads'],
    queryFn: () => запрос<Заливки>('/uploads'),
    ...ОБЩЕЕ,
  })
}
